import { useState, useEffect } from 'react';
import { CompanyIndustryCodeDto, CompanySearchDto } from 'models/dtoTypes';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import { CompanyDetailedDto } from 'models/dtoTypes';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { getSustainabilityScoreColor } from 'kosmosConfig';
import { getSustainabilityScore } from 'utils/score';
import BlockIcon from '@mui/icons-material/Block';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';

const PREFIX = 'KosmosCompanyResult';

const classes = {
  badge: `${PREFIX}-badge`,
  table: `${PREFIX}-table`,
  tableBody: `${PREFIX}-tableBody`,
  checkboxCell: `${PREFIX}-checkboxCell`,
  companyNameCell: `${PREFIX}-companyNameCell`,
  companyName: `${PREFIX}-companyName`,
  companyCode: `${PREFIX}-companyCode`,
  address: `${PREFIX}-address`,
  industryCodes: `${PREFIX}-industryCodes`,
  scoreCell: `${PREFIX}-scoreCell`,
  score: `${PREFIX}-score`,
  resultRow: `${PREFIX}-resultRow`,
  external: `${PREFIX}-external`,
};

const Root = styled('div')(({ theme }) => ({
  overflowY: 'scroll',
  display: 'flex',
  flexDirection: 'column',

  [`& .${classes.badge}`]: {
    width: '100%',
    height: 50,
    fontSize: 30,
    paddingLeft: 15,
    lineHeight: '50px',
  },

  [`& .${classes.table}`]: {
    width: '100%',
    padding: 0,
    borderCollapse: 'separate',
    borderSpacing: '0px 20px'
  },

  [`& .${classes.tableBody}`]: {
    padding: theme.spacing(6),
  },

  [`& .${classes.resultRow}`]: {
    border: 'none',
    display: 'flex',
  },
  [`& .${classes.checkboxCell}`]: {
    width: 100,
  },
  [`& .${classes.companyNameCell}`]: {
    flex: 1,
    '& a': {
      cursor: 'pointer',
    }
  },
  [`& .${classes.companyName}`]: {
    fontSize: 26,
  },

  [`& .${classes.score}`]: {
    cursor: 'pointer',
    backgroundColor: '#24c8a8',
    color: 'white',
    fontWeight: 'bold',
    width: 120,
    height: 65,
    border: '2px solid #24c8a8',
    padding: 0,
    margin: 'auto',
  },

  [`& .${classes.external}`]: {
    paddingLeft: theme.spacing(2)
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: 'black',
    fontWeight: 'bold',
    border: 'none',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    border: 'none',
  },
}));

interface CompanyItemProps {
  selected: boolean;
  onToggleCompanyId: (id: string, company?: CompanySearchDto) => void;
  company: CompanyDetailedDto;
  loading?: boolean;
  disabled?: boolean;
}

function CompanyItem(props: CompanyItemProps): JSX.Element {

  const {
    loading = false,
    selected,
    onToggleCompanyId,
    company,
  } = props;

  const [statusInfo, setStatusInfo] = useState(company.statusInfo);

  const {
    name = '',
    scoringState = 'NotStarted',
    address,
    companyIndustryCodes,
  } = company;

  useEffect(() => {
    // company.scoringState:  Pending(not started), InPorgress(started), Error, Active
    // state(statusInfo):     Request info        , Pending,             Error, Activ3
    // NOTE: click trigger button => setStatusInfo('Requesting')
    // Note: get first reply from api(start loading) => setStatusInfo('Pending')
    if (loading) {
      setStatusInfo('Pending');
    } else if (company.scoringState === 'Error') {
      setStatusInfo('Error');
    } else if (company.statusInfo === 'Pending') {
      setStatusInfo('Request info');
    } else if (company.statusInfo === 'Active') {
      setStatusInfo('Active');
    }
  }, [company, loading]);

  const isDissolved = statusInfo === 'Dissolved';

  const score = company.esgScore || getSustainabilityScore(company);

  const generateScoreWidgetStyle = () => {
    if (statusInfo === 'Dissolved') {
      return {
        backgroundColor: '#707071',
        border: '2px solid #707071',
      };
    } else if (statusInfo === 'Request info') {
      return {
        backgroundColor: 'white',
        border: '2px solid #24c8a8',
        color: '#24c8a8'
      };
    } else if (scoringState === 'Completed') {
      const color = getSustainabilityScoreColor(score || 0);
      return {
        border: `2px solid ${color || 'black'}`,
        backgroundColor: color || 'black',
      };
    } else {
      return {};
    }
  };

  const generateFullAddress = () => {
    if (!address) {
      return 'Unknown';
    }
    const { addressLine1, addressLine2, city, country, postalCode } = address;
    const AddrComponents = [addressLine1, addressLine2, city, country, postalCode];
    return AddrComponents.filter(c => c).join(', ');
  };

  const generateCompanyIndustryCodeList = (codeList: CompanyIndustryCodeDto[]) => {
    const primaryList = codeList.filter(code => code.isPrimary).map(code => code.description || '');
    const top2 = primaryList.slice(0, 2);
    return (
      <Stack direction="row" spacing={1}>
        {top2.map(codeDesc => <Chip key={codeDesc} label={codeDesc} />)}
      </Stack>
    );
  };

  const generateScoringState = () => {
    if (loading || statusInfo === 'Requesting') {
      return 'InProgress';
    }
    return isDissolved ? '' : scoringState;
  };

  const handleToggleCompanyId = (company: CompanySearchDto) => {
    onToggleCompanyId(company?.id || '', company);
  };

  return (
    <TableRow
      onClick={() => handleToggleCompanyId(company)}
      hover
      selected={selected}
      className={clsx(classes.resultRow, isDissolved ? 'dissolved' : null)}
    >
      <StyledTableCell className={classes.checkboxCell}>
        <Checkbox
          checked={selected}
          inputProps={{ 'aria-label': 'Checkbox demo' }}
        />
      </StyledTableCell>
      <StyledTableCell className={classes.companyNameCell}>
        <div>
          <Typography component="div" color="secondary" className={classes.companyName}>
            {name}
          </Typography>

          <div className={classes.address}>
            Address: {generateFullAddress()}
          </div>
          <div className={classes.address}>
            Source Company Id: {company.sourceCompanyId}
          </div>
          {company.registrationNumbers && !!company.registrationNumbers.length && <div>
            External Identifiers:<br />{company.registrationNumbers?.map((item) => <div className={classes.external} key={item.identifier}>{item.typeDescription}: {item.identifier} <br /></div>)}
          </div>}
          {companyIndustryCodes?.length
            ? (
              <div className={classes.industryCodes}>
                {generateCompanyIndustryCodeList(companyIndustryCodes)}
              </div>
            )
            : null
          }
        </div>
      </StyledTableCell>
      <StyledTableCell
        className={classes.scoreCell}
        align="center"
      >
        {generateScoringState() === 'Completed' || isDissolved && (
          <>
            <div style={{ marginBottom: 10 }}><b>ESG score</b></div>
            <div
              className={classes.score}
              style={generateScoreWidgetStyle()}
            >
              {generateScoringState() === 'Completed' && (
                <span style={{ fontSize: 30, lineHeight: 2 }}>{score.toString()}</span>
              )}
              {isDissolved && (
                <IconButton
                  color="inherit"
                  aria-label="delete"
                  size="large">
                  <BlockIcon fontSize="large" />
                </IconButton>
              )}
            </div>
            <div><b>Status:</b> {statusInfo || ''}</div>
          </>
        )}

      </StyledTableCell>
    </TableRow>
  );
}

interface Props {
  count?: number;
  companies: CompanySearchDto[];
  selectedCompanyIds: string[];
  onToggleCompanyId: (id: string, company?: CompanySearchDto) => void;
}

export default function SearchResultTable(props: Props): JSX.Element {
  const { count = 0, companies, onToggleCompanyId, selectedCompanyIds } = props;
  return (
    <Root>
      <div className={classes.badge}>{count === 1 ? '1 Result' : `${count} results`}</div>
      <Table className={classes.table} aria-label="company result table">
        <TableBody>
          {companies.map((c, i) => (
            <CompanyItem
              selected={selectedCompanyIds.includes(c?.id || '')}
              onToggleCompanyId={onToggleCompanyId}
              company={c}
              key={c?.id || i}
            />
          ))}
        </TableBody>
      </Table>
    </Root>
  );
}
